import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { catchError, map } from 'rxjs/operators';
import { NotificationService } from '../../../../../common/services';
import { ShipmentWaybillEquipmentsService } from './shipment-waybill-equipments.service';
import { Observable, of } from 'rxjs';
import { Error, CustomHttpErrorResponse, MeasurementUnitCodesEnum } from '../../../../../common';
import {
  ShipmentWaybillEquipmentsInitialState,
  ShipmentWaybillEquipmentsLoad,
} from './shipment-waybill-equipments.actions';
import { WaybillEquipmentItemModel, WaybillEquipmentsModel } from '../../../../models/waybill-equipments.model';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface ShipmentWaybillEquipmentsStateModel {
  equipments: WaybillEquipmentItemModel[];
  volumeUnitCode: MeasurementUnitCodesEnum;
  weightUnitCode: MeasurementUnitCodesEnum;
  dimensionsUnitCode: MeasurementUnitCodesEnum;
  loaded: boolean;
}

const initialState: ShipmentWaybillEquipmentsStateModel = {
  equipments: [],
  volumeUnitCode: null,
  weightUnitCode: null,
  dimensionsUnitCode: null,
  loaded: false,
};

@State<ShipmentWaybillEquipmentsStateModel>({
  name: 'shipmentWaybillEquipments',
  defaults: initialState,
})
@Injectable()
export class ShipmentWaybillEquipmentsState {
  constructor(
    private shipmentWaybillEndpointWaybillEquipmentsService: ShipmentWaybillEquipmentsService,
    private notificationService: NotificationService,
  ) {}

  @Selector()
  static getState(state: ShipmentWaybillEquipmentsStateModel): ShipmentWaybillEquipmentsStateModel {
    return state;
  }

  @Selector()
  static getLoaded(state: ShipmentWaybillEquipmentsStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static getEquipments(state: ShipmentWaybillEquipmentsStateModel): WaybillEquipmentItemModel[] {
    return state.equipments;
  }

  @Selector()
  static getVolumeUnitCode(state: ShipmentWaybillEquipmentsStateModel): MeasurementUnitCodesEnum {
    return state.volumeUnitCode;
  }

  @Selector()
  static getWeightUnitCode(state: ShipmentWaybillEquipmentsStateModel): MeasurementUnitCodesEnum {
    return state.weightUnitCode;
  }

  @Selector()
  static getDimensionsUnitCode(state: ShipmentWaybillEquipmentsStateModel): MeasurementUnitCodesEnum {
    return state.dimensionsUnitCode;
  }

  @Action(ShipmentWaybillEquipmentsLoad)
  getWaybillEquipments(
    ctx: StateContext<ShipmentWaybillEquipmentsStateModel>,
    { payload }: ShipmentWaybillEquipmentsLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.shipmentWaybillEndpointWaybillEquipmentsService.getShipmentWaybillTransportEquipments(payload).pipe(
      map((response: WaybillEquipmentsModel) => {
        ctx.patchState({
          equipments: [...response.items],
          weightUnitCode: response.weightUnitCode,
          volumeUnitCode: response.volumeUnitCode,
          dimensionsUnitCode: response.dimensionsUnitCode,
          loaded: true,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ShipmentWaybillEquipmentsInitialState)
  setInitialState(ctx: StateContext<ShipmentWaybillEquipmentsStateModel>): void {
    ctx.setState(initialState);
  }
}
